import React, { Component } from "react";
import PropTypes from "prop-types";

// MaterialUI
import { withStyles, withTheme } from "@material-ui/core";

// Redux
import { connect } from "react-redux";
import { getSettingsOpen } from "../../redux/selectors";
import {
  toggleSettingsOpen,
  closeSettings,
  setSettingsEnabled
} from "../../redux/actions";

// Other
import uuidv4 from "uuid";
import moment from "moment";

// Firewatch
import api from "../../services/Api";
import DataContextComponent, { DataContext } from "../DataContext";
import DashboardComponent, { ComponentContext } from "../DashboardComponent";
import SettingsMenu from "../controls/SettingsMenu";
import BaseChart from "./BaseChart";
import AdvancedChartSettings from "./AdvancedChartSettings";
import ChartDetails from "./ChartDetails";

import { getDateRanges, lzw_encode } from "../../helpers/DashboardHelpers";
import {
  getCategoryColor,
  getHexColor,
  resetNextCategory
} from "../../helpers/Colors";

const defaultSettings = {
  drawTitle: true,
  title: "Unnamed Chart",
  titlePosition: "top",
  drawDates: true,
  datePosition: "top",
  xAxisCategories: [],
  xAxisLines: [],
  yAxisText: "",
  yAxisLines: [],
  selectedColumns: [],
  selectedCategories: [],
  colors: {},
  type: "bar"
};

export default class StandaloneAdvancedChart extends Component {
  render() {
    return (
      <DataContextComponent>
        <DataContext.Consumer>
          {({ setDataSettings, getDataSettings, ...dataSettings }) => (
            // DashboardComponent is a DataContext consumer in this scenario because it needs to save data settings with dashboard items
            <DashboardComponent
              setDataSettings={setDataSettings}
              getDataSettings={getDataSettings}
              defaultSettings={defaultSettings}
              {...this.props}
            >
              <ComponentContext.Consumer>
                {({ setSettings, ...settings }) => (
                  <AdvancedChart
                    setSettings={setSettings}
                    setDataSettings={setDataSettings}
                    dashboardMode={false}
                    {...settings}
                    {...dataSettings}
                    {...this.props}
                  >
                    <AdvancedChartSettings {...settings} {...dataSettings} />
                  </AdvancedChart>
                )}
              </ComponentContext.Consumer>
            </DashboardComponent>
          )}
        </DataContext.Consumer>
      </DataContextComponent>
    );
  }
}

const styles = {};

class AdvancedChartComponent extends Component {
  constructor(props) {
    super(props);

    this.props.setSettings({ componentType: "Chart" });
  }

  state = { loading: true };

  componentDidMount = () => {
    if (!this.props.dashboardMode) {
      this.props.setSettingsEnabled(true);
      document.title = "Code3 Firewatch - Advanced Chart";
      this.setupInitialDates();
    } else {
      this.setState({ loading: false });
    }
  };

  componentDidUpdate = (prevProps, prevState) => {
    if (
      this.props.startDate !== prevProps.startDate ||
      this.props.endDate !== prevProps.endDate ||
      this.props.metric !== prevProps.metric ||
      this.props.calculation !== prevProps.calculation ||
      this.props.aggregation !== prevProps.aggregation ||
      this.props.category !== prevProps.category ||
      this.props.groupCategories !== prevProps.groupCategories ||
      this.props.calcParam1Name !== prevProps.calcParam1Name ||
      this.props.calcParam1 !== prevProps.calcParam1 ||
      this.props.calcParam2Name !== prevProps.calcParam2Name ||
      this.props.calcParam2 !== prevProps.calcParam2 ||
      this.props.selectedCategories !== prevProps.selectedCategories ||
      this.props.selectedColumns !== prevProps.selectedColumns ||
      this.props.dataFilters !== prevProps.dataFilters ||
      this.props.regionSelection !== prevProps.regionSelection ||
      this.state.loading !== prevState.loading
    ) {
      if (this.state.loading !== prevState.loading) {
        this.props.setSettings({ loading: this.state.loading });
      }
      if (this.state.loading === false) {
        this.updateData();
      }
    }

    if (this.props.data !== prevProps.data) {
      this.parseData();
    }
  };

  componentWillUnmount = () => {
    if (!this.props.dashboardMode) {
      this.props.setSettingsEnabled(false);
      this.props.closeSettings();
      document.title = "Code3 Firewatch";
    }
  };

  setupInitialDates = async () => {
    var dateRanges = await getDateRanges();
    var startMoment = moment(this.props.startDate);
    var maxMoment = moment(dateRanges.maxDate);
    var startDate = this.props.startDate || dateRanges.startDate;
    var endDate = this.props.endDate || dateRanges.endDate;
    if (startMoment > maxMoment) {
      endDate = maxMoment.format("YYYY-MM-DD");
      startDate = maxMoment.subtract(1, "months").format("YYYY-MM-DD");
    }

    this.props.setDataSettings({
      startDate: startDate,
      endDate: endDate,
      minDate: dateRanges.minDate,
      maxDate: dateRanges.maxDate
    });

    if (this.state.loading) {
      this.setState({ loading: false });
    }
  };

  updateData = async () => {
    let calculationParameters = "";
    if (this.props.calcParam1Name && this.props.calcParam1Name != "") {
      calculationParameters += `${this.props.calcParam1Name}=${this.props.calcParam1}`;
    }

    if (this.props.calcParam2Name && this.props.calcParam2Name != "") {
      calculationParameters += `${this.props.calcParam2Name}=${this.props.calcParam2}`;
    }

    let categoryValues = "";
    if (this.props.selectedCategories)
      categoryValues = this.props.selectedCategories.join(",");

    var regionIds = [];
    if (this.props.regionSelection !== undefined) {
      for (var i = 0; i < this.props.regionSelection.length; i++) {
        regionIds.push(this.props.regionSelection[i].id);
      }
    }

    const gettingData = api.getChartData(
      this.props.startDate,
      this.props.endDate,
      this.props.metric,
      this.props.calculation,
      this.props.aggregation,
      this.props.category,
      categoryValues,
      this.props.groupCategories,
      calculationParameters,
      this.props.dataFilters,
      regionIds
    );

    const gettingIncidents = api.getChartIncidents(
      this.props.startDate,
      this.props.endDate,
      this.props.metric,
      this.props.calculation,
      this.props.aggregation,
      this.props.category,
      categoryValues,
      this.props.groupCategories,
      calculationParameters,
      this.props.dataFilters,
      regionIds
    );

    gettingData.then(data => {
      this.props.setDataSettings({
        dataStart: this.props.startDate,
        dataEnd: this.props.endDate,
        data: data
      });
    });
    gettingIncidents.then(incidents => {
      this.props.setDataSettings({ incidents: incidents });
    });
  };

  parseData = async () => {
    let data = this.props.data;
    let selectedColumns = this.props.selectedColumns;

    resetNextCategory();

    let pivot = [];
    let columns = [];
    let colors = {};

    let column, propertyData, columnKeys, pivotElement;
    let groups = [];

    for (let categoryName in data) {
      colors[categoryName] = getHexColor(getCategoryColor(categoryName));
      propertyData = data[categoryName];
      columnKeys = Object.keys(propertyData);
      column = [];
      column.push(categoryName.toString());

      if (!columnKeys) {
        continue;
      }

      for (var i = 0; i < columnKeys.length; i++) {
        if (
          selectedColumns &&
          selectedColumns.length != 0 &&
          !selectedColumns.includes(columnKeys[i]) &&
          !selectedColumns.includes(Number.parseFloat(columnKeys[i]))
        ) {
          continue;
        }

        if (!groups.includes(columnKeys[i])) {
          pivot.push([columnKeys[i]]);
          groups.push(columnKeys[i]);
        }
      }

      for (var i = 0; i < groups.length; i++) {
        if (
          selectedColumns &&
          selectedColumns.length != 0 &&
          !selectedColumns.includes(groups[i]) &&
          !selectedColumns.includes(Number.parseFloat(groups[i]))
        ) {
          continue;
        }

        pivotElement = pivot[i];
        if (columnKeys.includes(groups[i])) {
          pivotElement.push(propertyData[groups[i]]);
          column.push(propertyData[groups[i]]);
        } else {
          pivotElement.push(0);
          column.push(0);
        }
      }

      columns.push(column);
    }

    var yAxisText;
    if (this.props.metric == "Utilization") {
      yAxisText = "Percent";
    } else if (this.props.calculation == "Count") {
      yAxisText = "Count";
    } else {
      yAxisText = "Minutes";
    }

    var dateText = this.props.startDate + " - " + this.props.endDate;

    this.props.setSettings({
      xAxisCategories: groups,
      pivot: pivot,
      columns: columns,
      colors: colors,
      yAxisText: yAxisText,
      loading: false,
      dateText: dateText
    });
  };

  render = () => {
    return (
      <div
        id="outer-container"
        style={
          (this.props.dashboardMode && {
            height: this.props.height ? this.props.height + "px" : "450px"
          }) ||
          {}
        }
      >
        {!this.props.dashboardMode && (
          <SettingsMenu
            pageWrapId={this.id}
            outerContainerId={"outer-container"}
            customBurgerIcon={false}
            onStateChange={this.menuStateChange}
            style={"push"}
            noOverlay={false}
            width={280}
          >
            {this.props.children}
          </SettingsMenu>
        )}
        <BaseChart
          id={this.id}
          dashboardMode={this.props.dashboardMode}
          height={this.props.height}
          width={this.props.width}
          showNewTabButton={this.props.dashboardMode}
          newTabFunction={this.openNewTab}
          showDetailsButton={!this.props.dashboardMode}
          drawerContents={<ChartDetails />}
        />
      </div>
    );
  };

  openNewTab = () => {
    let settings = this.props.dashboardSettings;
    settings.title = this.props.title;
    let objJsonStr = JSON.stringify(settings);
    let lzw = lzw_encode(objJsonStr);

    var win = window.open("/chart/advanced/" + lzw);
    win.focus();
  };
}

AdvancedChartComponent.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired
};

const mapStateToProps = state => {
  const settingsOpen = getSettingsOpen(state);
  return { settingsOpen };
};

const mapDispatchToProps = {
  toggleSettingsOpen,
  closeSettings,
  setSettingsEnabled
};

export const AdvancedChart = connect(
  mapStateToProps,
  mapDispatchToProps,
  null,
  { forwardRef: true }
)(withStyles(styles)(withTheme(AdvancedChartComponent)));
